import { AlertCircle, CalendarClock, Clock } from 'lucide-react';
import { cn } from '@/lib/cn';
import { deadlineTier } from '@/lib/format';

export function DeadlineBadge({ deadline }: { deadline: number | string | null | undefined }) {
  const { tier, days } = deadlineTier(deadline);
  if (days == null) {
    return (
      <span className="pill-neutral">
        <CalendarClock className="h-3 w-3" /> No deadline
      </span>
    );
  }
  if (tier === 'expired') {
    return (
      <span className="pill-danger" data-testid="deadline-badge">
        <AlertCircle className="h-3 w-3" /> Expired · {Math.abs(days)}d
      </span>
    );
  }
  if (tier === 'urgent') {
    return (
      <span className="pill-danger" data-testid="deadline-badge">
        <Clock className="h-3 w-3" /> {days}d left
      </span>
    );
  }
  if (tier === 'warn') {
    return (
      <span className="pill-warn" data-testid="deadline-badge">
        <Clock className="h-3 w-3" /> {days}d left
      </span>
    );
  }
  return (
    <span className="pill-success" data-testid="deadline-badge">
      <Clock className="h-3 w-3" /> {days}d left
    </span>
  );
}

/** Editorial display — big numeral for detail page */
export function CountdownDisplay({ deadline }: { deadline: number | string | null | undefined }) {
  const { tier, days } = deadlineTier(deadline);

  if (days == null) {
    return (
      <div className="flex flex-col items-end gap-1">
        <div className="text-[0.6875rem] font-semibold uppercase tracking-kicker text-ink-dim">
          Deadline
        </div>
        <div className="text-[2.5rem] font-bold text-ink-dim tabular-nums">—</div>
      </div>
    );
  }

  const color =
    tier === 'expired' || tier === 'urgent'
      ? 'text-danger-bold'
      : tier === 'warn'
        ? 'text-warn-bold'
        : 'text-brand-700';

  const label = tier === 'expired' ? 'Days past' : 'Days remaining';

  return (
    <div className="flex flex-col items-end gap-1">
      <div className="text-[0.6875rem] font-semibold uppercase tracking-kicker text-ink-dim">
        {label}
      </div>
      <div className={cn('flex items-baseline gap-2', color)}>
        <span
          className="text-[2.75rem] font-bold leading-none tracking-head tabular-nums"
          data-testid="deadline-badge"
        >
          {Math.abs(days)}
        </span>
        <span className="text-xs font-semibold uppercase tracking-[0.14em]">
          {tier === 'expired' ? 'past' : 'days'}
        </span>
      </div>
    </div>
  );
}

export function ConfidenceBadge({ confidence }: { confidence: number | null | undefined }) {
  if (confidence == null) {
    return (
      <span className="pill-neutral">No confidence</span>
    );
  }
  const pct = `${Math.round(confidence * 100)}%`;
  if (confidence < 0.7) {
    return (
      <span className="pill-warn" data-testid="confidence-badge">
        Confidence · {pct}
      </span>
    );
  }
  if (confidence < 0.85) {
    return (
      <span className="pill-info" data-testid="confidence-badge">
        Confidence · {pct}
      </span>
    );
  }
  return (
    <span className="pill-success" data-testid="confidence-badge">
      Confidence · {pct}
    </span>
  );
}
